/* eslint-disable no-spaced-func */
/* eslint-disable func-call-spacing */
import { createAsyncThunk } from "@reduxjs/toolkit";
import { assetsApi, mappingApi } from "@iventis/api/src/api";
import { ResponseError } from "@iventis/api/src/axios.types";
import { Map } from "@iventis/domain-model/model/map";
import { MapObject } from "@iventis/domain-model/model/mapObject";
import { PagedResult } from "@iventis/domain-model/model/pagedResult";
import { AxiosError } from "axios";
import { getMapLayers, getMapObjectAndCommentBounds } from "@iventis/api-helpers-mapping";
import { LocalGeoJson } from "@iventis/map-types";
import { queryParams } from "@iventis/utilities";
import { incrementMonitoringCount } from "@iventis/observability-and-monitoring";
import { MetricName } from "@iventis/domain-model/model/metricName";
import { Polygon } from "geojson";
import { getAssetsByType } from "@iventis/api-helpers";
import { AssetType } from "@iventis/domain-model/model/assetType";
import { Asset } from "@iventis/domain-model/model/asset";
import { IventisFilterOperator } from "@iventis/data-table";
import { StyleType } from "@iventis/domain-model/model/styleType";
import QueryString from "qs";
import { FilterFormat } from "@iventis/types";
import { convertMapObjectsToLocalGeoJson } from "./map.slice.get-map-functions";
import { getProjectDataFields, getSitemaps, getTerrains } from "./map.slice.thunks";
// eslint-disable-next-line iventis/no-root-store-dependencies
import { RootState, RootDispatch } from "./root.store";

/**
 * Calls all of the necessary thunks to load a map
 */
export async function loadMap(mapId: string, signal: AbortSignal, dispatch: RootDispatch) {
    dispatch(getMap({ mapId, signal }));
    dispatch(getProjectDataFields({ signal }));
    dispatch(getMapObjectsForMap({ mapId, signal }));
    dispatch(getSitemaps({ signal }));
    dispatch(getTerrains({ signal }));
    dispatch(getMapBackgrounds({ signal }));
}

/**
 * Gets a basic map
 */
export const getMap = createAsyncThunk<{ map: Map; tileBounds: { objects: Polygon; comments: Polygon } }, { mapId: string; signal: AbortSignal }, { state: RootState }>(
    "Map/getMap",
    async ({ mapId, signal }, { rejectWithValue }) => {
        try {
            const { data: map } = await mappingApi.get<Map>(`/maps/${mapId}`, { signal });
            const layers = await getMapLayers(mapId, mappingApi, signal);
            map.layers = layers;

            // If query params has no level, then get from default saved map view
            const selectedLevel = queryParams.level ?? map.savedMapViews.find((view) => view.id === map.defaultSavedMapViewId)?.level?.toString();
            const boundsResponse = await getMapObjectAndCommentBounds(mappingApi, mapId, selectedLevel, signal);
            const tileBounds = {
                objects: boundsResponse.mapObjectBounds,
                comments: boundsResponse.commentBounds,
            };
            incrementMonitoringCount({ name: MetricName.PLAN_LOADED, resourceId: mapId });
            return { map, tileBounds };
        } catch (err) {
            const error: AxiosError<ResponseError> = err;
            return rejectWithValue(error.response?.data);
        }
    }
);

/**
 * Gets and converts one page of local map objects.
 */
export const getMapObjectsPage = createAsyncThunk<
    { geoJSON: LocalGeoJson; lastPage: boolean },
    { mapId: string; mapObjectFilter: FilterFormat[]; pageNumber: number; signal: AbortSignal },
    { state: RootState }
>("Map/getMapObjectsPage", async ({ mapId, mapObjectFilter, pageNumber, signal }, { rejectWithValue }) => {
    try {
        const { data: pageOfMapObjects } = await mappingApi.get<PagedResult<MapObject[]>>(`/maps/${mapId}/map_objects/filter`, {
            params: {
                filter: JSON.stringify(mapObjectFilter),
                mapId,
                pageNumber,
            },
            paramsSerializer: (params) => QueryString.stringify(params, { arrayFormat: "repeat" }),
            signal,
        });
        return { geoJSON: convertMapObjectsToLocalGeoJson(pageOfMapObjects.results), lastPage: pageOfMapObjects.lastPage };
    } catch (err) {
        const error: AxiosError<ResponseError> = err;
        return rejectWithValue(error.response?.data);
    }
});

/**
 * Gets every local model and distance-marker map object for a map.
 * This is necessary because the map engine can't render models or markers on remote objects.
 */
export const getMapObjectsForMap = createAsyncThunk<void, { mapId: string; signal: AbortSignal }, { state: RootState }>(
    "Map/getMapObjectsForMap",
    async ({ mapId, signal }, { dispatch }) => {
        const mapObjectFilters: FilterFormat[][] = [
            // All model map objects (including line and area models)
            [{ fieldName: "StyleType", operator: IventisFilterOperator.In, value: [StyleType.Model, StyleType.LineModel, StyleType.AreaModel] }],
            // All distance-marker map objects
            [{ fieldName: "distanceMarker", operator: IventisFilterOperator.Eq, value: true }],
        ];

        await Promise.all(
            mapObjectFilters.map(async (mapObjectFilter) => {
                let pageNumber = 1;
                let lastPage = false;

                while (!lastPage) {
                    const page = await dispatch(getMapObjectsPage({ mapId, mapObjectFilter, pageNumber, signal })).unwrap();
                    lastPage = page.lastPage;
                    pageNumber += 1;
                }
            })
        );
    }
);

/**
 * Gets the bounds of the map objects and comments for a given map
 */
export const getMapObjectAndCommentBoundsThunk = createAsyncThunk<
    { mapObjectBounds: Polygon | ""; commentBounds: Polygon | "" },
    { mapId: string; level: string; signal: AbortSignal },
    { state: RootState }
>("Map/getMapObjectAndCommentBoundsThunk", async ({ mapId, level, signal }, { rejectWithValue }) => {
    try {
        const { mapObjectBounds, commentBounds } = await getMapObjectAndCommentBounds(mappingApi, mapId, level, signal);
        return { mapObjectBounds, commentBounds };
    } catch (err) {
        const error: AxiosError<ResponseError> = err;
        return rejectWithValue(error.response?.data);
    }
});

export const getMapBackgrounds = createAsyncThunk<Asset[], { signal: AbortSignal }>("Map/getMapBackgrounds", async ({ signal }, { rejectWithValue }) => {
    try {
        const mapBackgroundAssets = await getAssetsByType([AssetType.MapBackground], assetsApi, signal);
        return mapBackgroundAssets;
    } catch (err) {
        const error: AxiosError<ResponseError> = err;
        return rejectWithValue(error.response?.data);
    }
});
